import React, { useMemo } from "react";
import { Attachment } from "protogen/common_pb";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { withImageProps } from "./SignedImageCache";
import DocViewer, {
  DocRenderer,
  DocViewerRenderers,
} from "@cyntler/react-doc-viewer";
import useIsMobile from "../hooks/useIsMobile";
import { Download } from "lucide-react";
import { DocRendererProps } from "@cyntler/react-doc-viewer/dist/esm/models";

// React-doc-viewer uses MSFT which breaks all the time. Let's try google!
//https://github.com/Alcumus/react-doc-viewer/issues/123
const CustomFileRender: DocRenderer = ({
  mainState: { currentDocument },
}: DocRendererProps) => {
  if (!currentDocument) return null;
  const googleDocsViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
    currentDocument.uri,
  )}&embedded=true`;

  return (
    <div
      style={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: "white",
      }}
    >
      <iframe
        width="100%"
        height="100%"
        src={googleDocsViewerUrl}
        style={{
          border: "none",
        }}
      ></iframe>
    </div>
  );
};

// Define the file types that this renderer will handle
CustomFileRender.fileTypes = [
  "pdf",
  "application/pdf",
  "xls",
  "application/vnd.ms-excel",
  "xlsx",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "ppt",
  "application/vnd.ms-powerpoint",
  "pptx",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "doc",
  "application/msword",
  "docx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
CustomFileRender.weight = 1;

interface DocumentProp {
  uri: string;
  fileName: string;
  fileType: string;
}

interface MemoizedDocViewerProps {
  document: DocumentProp;
}

// DocViewer does not handle re-renders well when props change
// renders blank page.
const MemoizedDocViewer = React.memo(({ document }: MemoizedDocViewerProps) => {
  return (
    <DocViewer
      prefetchMethod="GET"
      documents={[document]}
      pluginRenderers={[CustomFileRender, ...DocViewerRenderers]}
    />
  );
});

const IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif|svg)$/i;

const isImage = (attachment: Attachment) => {
  return (
    attachment.contentType.startsWith("image/") ||
    IMAGE_EXTENSIONS.test(attachment.filename || "")
  );
};

const isDocument = (attachment: Attachment) => {
  const documentTypes = new Set([
    // Note: Pain to get their image renderer to work correctly.
    // "image/bmp",
    // "image/gif",
    // "image/jpg",
    // "image/jpeg",
    // "image/png",
    // "image/tiff",
    "application/vnd.oasis.opendocument.text",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/pdf",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/csv",
    "text/htm",
    "text/html",
    "text/plain",
    "video/mp4",
  ]);
  return documentTypes.has(attachment.contentType.toLowerCase());
};

export const isSupportedAttachment = (attachment: Attachment) => {
  return isImage(attachment) || isDocument(attachment);
};

const DocAttachment = ({ attachment }: { attachment: Attachment }) => {
  // Handle memoization and attachment parsing.
  const memoizedDocument = useMemo(() => {
    if (!attachment) return null;
    return {
      uri: attachment.url,
      fileName: attachment.filename,
      fileType: attachment.contentType,
    };
  }, [attachment]);
  if (!memoizedDocument) return null;
  return <MemoizedDocViewer document={memoizedDocument} />;
};

const ImageAttachment = ({
  attachment,
  isMobile,
}: {
  attachment: Attachment;
  isMobile: boolean;
}) => {
  const imageProps = withImageProps({ url: attachment.url! });

  const downloadS3File = async () => {
    try {
      const response = await fetch(attachment.url, { mode: "cors" });

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      // Determine extension from content type if not present
      const hasExtension = /\.[^/.]+$/.test(attachment.filename);
      const isTwilio = attachment.filename.includes("twilio");
      const inferredExtension = attachment.contentType
        ? attachment.contentType.split("/")[1]
        : "";

      let finalFilename = attachment.filename;
      if ((!hasExtension || isTwilio) && inferredExtension) {
        const timestamp = new Date().getTime();
        finalFilename = `download-${timestamp}.${inferredExtension}`;
      }

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download failed, opening in new tab instead:", err);
      window.open(attachment.url, "_blank");
    }
  };
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        backgroundColor: "transparent",
        padding: isMobile ? "0" : "20px",
        position: "relative", // For positioning the download button
      }}
    >
      <img
        {...imageProps}
        alt={attachment.filename}
        style={{
          maxWidth: isMobile ? "100%" : "80vw",
          maxHeight: isMobile ? "100%" : "80vh",
          objectFit: "cover",
          borderRadius: "8px",
        }}
      />
      <IconButton
        aria-label="download"
        sx={{
          position: "absolute",
          bottom: 30,
          right: 30,
          backgroundColor: "#e5e5e5",
          padding: "5px",
          "&:hover": {
            backgroundColor: "rgb(229, 229, 229, 0.6)",
          },
        }}
        onClick={async (e) => {
          e.preventDefault();
          // Chrome blocks the download of files from the same origin so we do this
          // programmatically.
          await downloadS3File();
        }}
      >
        <Download />
      </IconButton>
    </div>
  );
};

export const ImageURLDialog = ({
  attachmentUrl,
  onClose,
}: {
  attachmentUrl: string;
  onClose: () => void;
}) => {
  // Hooks are always called at the top level of the component, even if no attachment is provided.
  const isMobile = useIsMobile();

  return (
    <Dialog
      open={!!attachmentUrl}
      onClose={onClose}
      fullScreen={isMobile} // Full screen on mobile
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "auto",
          height: isMobile ? "100%" : "auto",
          maxWidth: "100%",
          maxHeight: "100%",
          position: "relative",
          borderRadius: isMobile ? 0 : "16px",
          padding: isMobile ? "10vh 10vw" : "35px",
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          padding: "4px",
          backgroundColor: "#e5e5e5",
          zIndex: 10,
          color: "#000",
          "&:hover": {
            backgroundColor: "#e5e5e5",
          },
          ...(isMobile
            ? {
                top: 24,
                right: 24,
                color: "#000",
              }
            : {}),
        }}
      >
        <CloseIcon style={{ color: "black" }} />
      </IconButton>
      <img src={attachmentUrl} alt="Attachment" />
    </Dialog>
  );
};

export default ({
  attachment,
  onClose,
}: {
  attachment: Attachment | null;
  onClose: () => void;
}) => {
  // Hooks are always called at the top level of the component, even if no attachment is provided.
  const isMobile = useIsMobile();
  // Return null early if the attachment is invalid, but the hooks have already been called
  if (!attachment || !isSupportedAttachment(attachment)) {
    return null;
  }
  return (
    <Dialog
      open={!!attachment}
      onClose={onClose}
      fullScreen={isMobile} // Full screen on mobile
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : isImage(attachment) ? "auto" : "60vw",
          height: isMobile ? "100%" : isImage(attachment) ? "auto" : "80vh",
          maxWidth: "100%",
          maxHeight: "100%",
          position: "relative",
          borderRadius: isMobile ? 0 : "16px",
          padding: isMobile ? "10vh 10vw" : "35px",
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          padding: "4px",
          backgroundColor: "#e5e5e5",
          zIndex: 10,
          color: "#000",
          "&:hover": {
            backgroundColor: "#e5e5e5",
          },
          ...(isMobile
            ? {
                top: 24,
                right: 24,
                color: "#000",
              }
            : {}),
        }}
      >
        <CloseIcon style={{ color: "black" }} />
      </IconButton>

      {/* Render Document */}
      {isDocument(attachment) && <DocAttachment attachment={attachment} />}
      {/* Render Image */}
      {isImage(attachment) && (
        <ImageAttachment attachment={attachment} isMobile={isMobile} />
      )}
    </Dialog>
  );
};
